<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import notificationService from '@/services/notificationService';
import notificationWebSocketService from '@/services/notificationWebSocketService';

const router = useRouter();
const toast = useToast();

const notifications = ref([]);
const notificationMenuActive = ref(false);

const notificationCount = computed(() => notifications.value.length);

const notificationCountDisplay = computed(() => {
  const count = notificationCount.value;
  return count > 99 ? '99+' : count;
});

const adicionarNotificacaoWebSocket = (data) => {
  const notificacao = {
    id: `notification-${Date.now()}-${Math.random()}`,
    attributes: {
      title: data.title || '📬 Nova Notificação',
      message: data.mensagem || data.message || '',
      createdAt: data.timestamp || new Date().toISOString(),
      read: false,
      tipo: data.tipo || 'notificacao',
      canal: data.canal,
      sender_name: data.usuarioEnvio || data.sender_name,
      actionUrl: data.actionUrl || data.project_link,
      project_link: data.project_link,
      actionLabel: data.actionLabel,
      ...data
    }
  };
  
  const exists = notifications.value.some(n => n.id === notificacao.id);
  if (!exists) {
    notifications.value.unshift(notificacao);
  }
};

const dismissNotification = async (notification) => {
  try {
    const isWebSocketNotification = notification.id.startsWith('notification-');
    
    if (!isWebSocketNotification) {
      await notificationService.remove(notification.id);
    }
    
    notifications.value = notifications.value.filter(
      (n) => n.id !== notification.id
    );
  } catch (error) {
    toast.error('Erro ao dispensar notificação');
  }
};

const handleNotificationAction = async (notification) => {
  
  try {
    const actionUrl = notification.attributes.actionUrl || 
                      notification.attributes.project_link || 
                      notification.attributes.extraData?.actionUrl;
    
    if (actionUrl) {
      notificationMenuActive.value = false;
      
      if (actionUrl.startsWith('http')) {
        window.open(actionUrl, '_blank');
      } else if (actionUrl.startsWith('/#/')) {
        const path = actionUrl.replace('/#', '');
        router.push(path);
      } else if (actionUrl.startsWith('/')) {
        router.push(actionUrl);
      } else {
        router.push(`/${actionUrl}`);
      }
      
      toast.info('Redirecionando...');
    }
  } catch (error) {
    toast.error('Erro ao abrir notificação');
  }
};

const dismissAll = async () => {
  try {
    for (const notification of notifications.value) {
      const isWebSocketNotification = notification.id.startsWith('notification-');
      if (!isWebSocketNotification) {
        await notificationService.remove(notification.id);
      }
    }
    notifications.value = [];
    toast.success('Todas as notificações foram dispensadas');
  } catch (error) {
    toast.error('Erro ao dispensar notificações');
  }
};

const toggleNotificationMenu = (event) => {
  event.stopPropagation();
  notificationMenuActive.value = !notificationMenuActive.value;
};

const handleOutsideClick = (event) => {
  if (notificationMenuActive.value && !event.target.closest('.notification-container')) {
    notificationMenuActive.value = false;
  }
};

onMounted(async () => {
  document.addEventListener('click', handleOutsideClick);
  
  const userId = localStorage.getItem('idUser');
  
  if (!userId) return;

  try {
    notificationWebSocketService.off('onNotification', adicionarNotificacaoWebSocket);
    notificationWebSocketService.on('onNotification', adicionarNotificacaoWebSocket);
    await notificationWebSocketService.conectarCanalUsuario(userId);
  } catch (error) {
    console.error('Erro ao conectar WebSocket:', error);
  }

  try {
    const response = await notificationService.list({ 
      read: false,
      target: userId
    });
    
    if (response.data && response.data.length > 0) {
      notifications.value = response.data;
    }
  } catch (error) {
    console.error('Erro ao carregar notificações:', error);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
  notificationWebSocketService.desconectar();
});
</script>

<template>
  <div class="notification-container">
    <button
      @click="toggleNotificationMenu"
      class="p-link layout-topbar-button notification-bell"
      aria-label="Notificações"
    >
      <i class="pi pi-bell"></i>
      <span v-if="notificationCount > 0" class="notification-badge">
        {{ notificationCountDisplay }}
      </span>
    </button>

    <div v-if="notificationMenuActive" class="notification-menu">
      <div class="notification-header">
        <h3>Notificações</h3>
        <button
          v-if="notifications.length > 0"
          class="p-link text-sm text-gray-500"
          @click="dismissAll"
        >
          Dispensar todas
        </button>
      </div>

      <div v-if="notifications.length === 0" class="notification-empty">
        Nenhuma notificação nova
      </div>

      <div v-else class="notification-list">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{ 
            'websocket-notification': notification.attributes.tipo === 'notificacao',
            'clickable': notification.attributes.actionUrl || notification.attributes.project_link || notification.attributes.extraData?.actionUrl
          }"
        >
          <div 
            class="notification-content"
            :class="{ 'has-action': notification.attributes.actionUrl || notification.attributes.project_link || notification.attributes.extraData?.actionUrl }"
            @click="notification.attributes.actionUrl || notification.attributes.project_link || notification.attributes.extraData?.actionUrl ? handleNotificationAction(notification) : null"
          >
            <div class="notification-text">
              <p class="notification-title">
                <i 
                  v-if="notification.attributes.tipo === 'projeto'" 
                  class="pi pi-briefcase notification-icon"
                ></i>
                <i 
                  v-else-if="notification.attributes.tipo === 'tarefa'" 
                  class="pi pi-check-square notification-icon"
                ></i>
                <i 
                  v-else-if="notification.attributes.tipo === 'alerta'" 
                  class="pi pi-exclamation-triangle notification-icon"
                ></i>
                <i 
                  v-else 
                  class="pi pi-bell notification-icon"
                ></i>
                {{ notification.attributes.title }}
              </p>
              
              <p v-if="notification.attributes.message" class="notification-message">
                {{ notification.attributes.message }}
              </p>
              
              <div class="notification-meta">
                <small>{{ notification.attributes.timeAgo || "agora mesmo" }}</small>
              </div>
            </div>
          </div>
          
          <div class="notification-actions">
            <button 
              class="p-link dismiss-btn" 
              @click.stop="dismissNotification(notification)"
              title="Dispensar"
            >
              <i class="pi pi-times"></i>
            </button>
            
            <div 
              v-if="notification.attributes.actionUrl || notification.attributes.project_link || notification.attributes.extraData?.actionUrl" 
              class="notification-action"
              @click.stop="handleNotificationAction(notification)"
            >
              <i class="pi pi-arrow-right"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./styles/NotificationMenu.styles.scss"></style>
